import React, { useState, useEffect } from 'react';
import { Nav, Button } from 'react-bootstrap';
import { FaPhone, FaEnvelope, FaStickyNote, FaCalendarAlt, FaPlus } from 'react-icons/fa';
import { formatDate } from '../../utils/formatUtils';
import { theme } from '../../theme/theme.js';
import './css/TaskActivityTimeline.css';

// Helper functions for activity icons and colors
const getActivityIcon = (type) => {
  switch (type.toLowerCase()) {
    case 'call':
      return FaPhone;
    case 'meeting':
      return FaCalendarAlt;
    case 'note':
      return FaStickyNote;
    case 'email':
      return FaEnvelope;
    default:
      return FaStickyNote;
  }
};

const getActivityColor = (type) => {
  switch (type.toLowerCase()) {
    case 'call':
      return 'blue';
    case 'meeting':
      return 'purple';
    case 'note':
      return 'grey';
    case 'email':
      return 'green';
    default:
      return 'grey';
  }
};

// Transform related list records format to activity format
const transformRecordsToActivities = (records) => {
    if (!records || !Array.isArray(records)) {
      console.log('transformRecordsToActivities - Invalid records:', records);
      return [];
    }

    console.log('transformRecordsToActivities - Processing', records.length, 'records');

    return records.map((record, index) => {
      console.log(`transformRecordsToActivities - Record ${index}:`, record);

      // Convert either:
      // - related list shape: { id, fields: [{fieldName, value, displayValue, ...}] }
      // - fetchRecords shape: { fieldName: {value, displayValue, referenceObject, ...} | primitive }
      const activity = {};
      if (record && typeof record === 'object') {
        // Record id can be string or {value,...}
        if (record.id && typeof record.id === 'object' && 'value' in record.id) {
          activity.id = record.id.value;
        } else {
          activity.id = record.id;
        }
      }

      if (record?.fields && Array.isArray(record.fields)) {
        console.log(
          `transformRecordsToActivities - Record ${index} has ${record.fields.length} fields`
        );
        record.fields.forEach(field => {
          if (!field?.fieldName) return;
          // Use displayValue if available (for lookup fields)
          activity[field.fieldName] =
            field.displayValue !== undefined && field.displayValue !== null
              ? field.displayValue
              : field.value;
        });
      } else if (record && typeof record === 'object') {
        // fetchRecords row shape
        Object.keys(record).forEach(key => {
          if (key === 'id') return;
          const v = record[key];
          if (v && typeof v === 'object') {
            // Prefer displayValue if present, else value, else fall back
            if ('displayValue' in v && v.displayValue !== null && v.displayValue !== undefined) {
              activity[key] = v.displayValue;
            } else if ('value' in v) {
              activity[key] = v.value;
            } else {
              activity[key] = v;
            }
          } else {
            activity[key] = v;
          }
        });
      } else {
        console.warn(
          `transformRecordsToActivities - Record ${index} is not an object:`,
          record
        );
      }

      console.log(`transformRecordsToActivities - Record ${index} transformed activity:`, activity);

      // Helper function to extract value from field (handles both string and object formats)
      const extractValue = (field) => {
        if (!field) return '';
        if (typeof field === 'object' && field !== null && 'value' in field) {
          return field.value || '';
        }
        return String(field || '');
      };

      // Get activity_type and parent_object, handling both lowercase and original case
      // Handle both string values and object formats (with 'value' property)
      const rawActivityType = extractValue(activity.activity_type);
      const rawParentObject = extractValue(activity.parent_object);
      const activityType = rawActivityType.toLowerCase();
      const parentObject = rawParentObject.toLowerCase();

      // Determine icon and color based on parent_object and activity_type
      let iconType = activityType;
      if (parentObject === 'email_log') {
        iconType = 'email';
      } else if (parentObject === 'note' || rawParentObject === 'Note') {
        // Notes: parent_object is 'Note' (capital) or 'note' (lowercase)
        iconType = 'note';
      } else if (parentObject === 'task') {
        iconType = activityType || 'note';
      }

      // Ensure notes are properly identified even if activity_type is not set
      // Check both lowercase and original case
      if ((parentObject === 'note' || rawParentObject === 'Note' || rawActivityType === 'Note') && !iconType) {
        iconType = 'note';
      }

      // Extract field values that might be objects
      const activityName = extractValue(activity.name);
      const status = extractValue(activity.status);
      const parentId = extractValue(activity.parent_id);
      const description = extractValue(activity.description);
      const activityDate = extractValue(activity.activity_date);
      const createdDate = extractValue(activity.createddate);

      // Determine title based on activity type and parent object
      let title = activityName || '';
      if (!title || title === 'Untitled') {
        if (parentObject === 'email_log') {
          title = activityName || 'Email';
        } else if (parentObject === 'note') {
          title = activityName || 'Note';
        } else if (parentObject === 'task') {
          title = activityName || 'Task';
        } else {
          title = activityName || 'Activity';
        }
      }

      return {
        id: activity.id,
        type: iconType,
        activityType: iconType,
        parentObject: parentObject,
        parentId: parentId,
        title: title || activityName,
        description: description,
        date: activityDate || createdDate,
        status: status,
        icon: getActivityIcon(iconType),
        color: getActivityColor(iconType),
        record: activity
      };
    });
};

/**
 * TaskActivityTimeline Component
 * Displays a timeline of activities (tasks, emails, notes) related to a record
 * Records must come from related list query - no duplicate API calls
 * @param {string} recordId - The record ID (for Log Activity button)
 * @param {string} objectName - The object name (for Log Activity button)
 * @param {Function} onRefresh - Callback when refresh is needed
 * @param {boolean} hideLogActivityButton - Whether to hide the log activity button
 * @param {Array} records - Pre-fetched records from related list query (required)
 */
const TaskActivityTimeline = ({ recordId, objectName, onRefresh, hideLogActivityButton = false, records = [] }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    // Always use records from related list query
    setLoading(true);
    console.log('TaskActivityTimeline - Received records:', records);
    console.log('TaskActivityTimeline - Records type:', typeof records, Array.isArray(records));
    console.log('TaskActivityTimeline - Records length:', records?.length);
    if (records && records.length > 0) {
      console.log('TaskActivityTimeline - First record structure:', records[0]);
      console.log('TaskActivityTimeline - First record fields:', records[0]?.fields);
    }
    const transformedActivities = transformRecordsToActivities(records || []);
    console.log('TaskActivityTimeline - Transformed activities:', transformedActivities);
    // Sort by date (newest first)
    transformedActivities.sort((a, b) => new Date(b.date) - new Date(a.date));
    setActivities(transformedActivities);
    setLoading(false);
  }, [records]);

  const groupActivitiesByDate = (activities) => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    activities.forEach(activity => {
      const activityDate = new Date(activity.date);
      activityDate.setHours(0, 0, 0, 0);

      let dateLabel;
      if (activityDate.getTime() === today.getTime()) {
        dateLabel = 'Today';
      } else if (activityDate.getTime() === yesterday.getTime()) {
        dateLabel = 'Yesterday';
      } else if (activityDate > today) {
        // Future dates - all grouped under "Upcoming"
        dateLabel = 'Upcoming';
      } else {
        // All other past dates grouped under "Past"
        dateLabel = 'Past';
      }

      if (!groups[dateLabel]) {
        groups[dateLabel] = [];
      }
      groups[dateLabel].push(activity);
    });

    // Sort activities within each group
    Object.keys(groups).forEach(dateLabel => {
      if (dateLabel === 'Upcoming') {
        // For upcoming activities, sort by date ascending (earliest first)
        groups[dateLabel].sort((a, b) => new Date(a.date) - new Date(b.date));
      } else {
        // For past activities (Today, Yesterday, Past), sort by date descending (newest first)
        groups[dateLabel].sort((a, b) => new Date(b.date) - new Date(a.date));
      }
    });

    return groups;
  };

  const filteredActivities = activeTab === 'all'
    ? activities
    : activities.filter(a => {
        const activityType = (a.activityType || '').toLowerCase();
        const parentObject = (a.parentObject || '').toLowerCase();
        if (activeTab === 'notes') {
          // Check for notes: activityType is 'note' OR parentObject is 'note' OR activity_type field was 'Note'
          return activityType === 'note' ||
                 activityType === 'notes' ||
                 parentObject === 'note' ||
                 (a.record && (a.record.parent_object === 'Note' || a.record.activity_type === 'Note'));
        }
        if (activeTab === 'calls') return activityType === 'call' || activityType === 'calls';
        if (activeTab === 'meetings') return activityType === 'meeting' || activityType === 'meetings';
        if (activeTab === 'emails') return activityType === 'email' || parentObject === 'email_log';
        return true;
      });

  const groupedActivities = groupActivitiesByDate(filteredActivities);

  // Sort date groups: Upcoming first, then Today, Yesterday, then Past
  const sortedDateGroups = Object.entries(groupedActivities).sort(([dateA], [dateB]) => {
    const order = ['Upcoming', 'Today', 'Yesterday', 'Past'];
    const indexA = order.indexOf(dateA);
    const indexB = order.indexOf(dateB);

    // If both are in the order array, sort by their index
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in the order array, it comes first
    if (indexA !== -1) return -1;
    // If only B is in the order array, it comes first
    if (indexB !== -1) return 1;
    // Both are not in order array (shouldn't happen), maintain current order
    return 0;
  });

  const handleLogActivity = () => {
    // Navigate to create task page with pre-filled related record
    window.location.href = `/edit/task?targetField=related_record_id&targetId=${recordId}&parentObjectName=${objectName}`;
  };

  if (loading) {
    return (
      <div className="task-activity-timeline">
        <div className="activity-timeline-loading">Loading activities...</div>
      </div>
    );
  }

  return (
    <div className="task-activity-timeline">
      <div className="activity-timeline-header">
        <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="activity-tabs">
          <Nav.Item>
            <Nav.Link eventKey="all">All</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="notes">Notes</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="calls">Calls</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="meetings">Meetings</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="emails">Emails</Nav.Link>
          </Nav.Item>
        </Nav>
        {!hideLogActivityButton && (
          <Button
            variant="primary"
            size="sm"
            className="log-activity-btn"
            onClick={handleLogActivity}
          >
            <FaPlus className="me-1" />
            Log Activity
          </Button>
        )}
      </div>

      <div className="activity-timeline-content">
        {sortedDateGroups.length === 0 ? (
          <div className="no-activities">
            <p>No activities found</p>
          </div>
        ) : (
          sortedDateGroups.map(([dateLabel, dateActivities]) => (
            <div key={dateLabel} className="activity-date-group">
              <h5 className="activity-date-label">{dateLabel}</h5>
              <div className="activity-timeline-line">
                {dateActivities.map((activity, index) => {
                  const IconComponent = activity.icon;
                  // For upcoming and past activities, show date below the title
                  const showDateBelow = dateLabel === 'Upcoming' || dateLabel === 'Past';

                  // Determine detail page URL based on activity type
                  const getDetailPageUrl = () => {
                    if (activity.parentObject === 'email_log' && activity.parentId) {
                      return `/view/email_log/${activity.parentId}`;
                    } else if (activity.parentObject === 'note' && activity.parentId) {
                      return `/view/note/${activity.parentId}`;
                    } else if (activity.parentObject === 'task' && activity.parentId) {
                      return `/view/task/${activity.parentId}`;
                    } else if (activity.id) {
                      // Fallback to activity detail page
                      return `/view/activity/${activity.id}`;
                    }
                    return null;
                  };

                  const detailUrl = getDetailPageUrl();

                  const handleActivityClick = (e) => {
                    // Don't navigate if clicking on the email link
                    if (e.target.closest('a')) {
                      return;
                    }
                    if (detailUrl) {
                      window.open(detailUrl, '_blank');
                    }
                  };

                  return (
                    <div
                      key={activity.id || index}
                      className="activity-item"
                      onClick={handleActivityClick}
                      style={{ cursor: detailUrl ? 'pointer' : 'default' }}
                    >
                      <div className={`activity-icon activity-icon-${activity.color}`}>
                        <IconComponent />
                      </div>
                      <div className="activity-content">
                        <div className="activity-title">
                          {(() => {
                            const activityType = (activity.activityType || '').toLowerCase();
                            // Ensure status is a string before calling toUpperCase
                            const status = String(activity.status || '').toUpperCase();

                            if ((activityType === 'call' || activityType === 'calls') && (status === 'CANCELLED' || status === 'MISSED')) {
                              return (
                                <>
                                  Call with {activity.title} - <span className="missed-call">Missed call</span>
                                </>
                              );
                            } else if (activityType === 'email' || activity.parentObject === 'email_log') {
                              // Make email subject (title) clickable to navigate to email_log detail page
                              const emailLogId = activity.parentId;
                              if (emailLogId) {
                                return (
                                  <>
                                    Email "{' '}
                                    <a
                                      href={`/view/email_log/${emailLogId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: theme?.colors?.primary || '#0176d3',
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.textDecoration = 'underline';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.textDecoration = 'none';
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {activity.title}
                                    </a>
                                    " sent
                                  </>
                                );
                              } else {
                                return (
                                  <>
                                    Email "{activity.title}" sent
                                  </>
                                );
                              }
                            } else if (activityType === 'note' || activityType === 'notes' || activity.parentObject === 'note') {
                              return (
                                <>
                                  Note added: {activity.title}
                                </>
                              );
                            } else if (activityType === 'meeting' || activityType === 'meetings') {
                              return (
                                <>
                                  Meeting: {activity.title}
                                </>
                              );
                            } else {
                              return activity.title;
                            }
                          })()}
                        </div>
                        {/* Show date below title for upcoming and past activities */}
                        {showDateBelow && (
                          <div className="activity-date" style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                            {formatDate(activity.date)}
                          </div>
                        )}
                        {/* Hide description for email activities, show for others */}
                        {activity.description && activity.parentObject !== 'email_log' && (
                          <div
                            className="activity-description"
                            dangerouslySetInnerHTML={{
                              __html: activity.parentObject === 'note'
                                ? activity.description
                                : activity.description.replace(/\n/g, '<br>')
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TaskActivityTimeline;
